import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store/store";

type TravelDetailsType = Record<string, any>;
type PersonalDetailsType = Record<string, any>;

export type QuoteFlowPaymentStatus = "IDLE" | "PROCESSING" | "SUCCESS" | "FAILED";
export type QuoteFlowPolicyResultStatus = "SUCCESS" | "SUCCESS_POLICY_FAILED" | "FAILED";

export type QuoteFlowPolicyResult = {
  paymentStatus: QuoteFlowPolicyResultStatus;
  apiStatus?: string;
  name?: string;
  email?: string;
  phone?: string;
  insurerName?: string;
  insurerLogo?: string;
  policyNumber?: string;
  policyPeriod?: string;
  premium?: string | number;
  vat?: string | number;
  totalAmount?: string | number;
  failureReason?: string;
  referenceId?: string;
  currency?: string;
  addOns?: Array<{
    label: string;
    amount?: number;
    value?: number;
  }>;
};

export type QuoteFlowState = {
  quoteData: any | null;
  bindData: any | null;
  travelDetails: Partial<TravelDetailsType> | null;
  personalDetails: Partial<PersonalDetailsType> | Record<string, any> | null;
  sessionData: any | null;
  moreInfoDraft: Record<string, any> | null;
  paymentStatus: QuoteFlowPaymentStatus;
  policyResult: QuoteFlowPolicyResult | null;
};

const initialState: QuoteFlowState = {
  quoteData: null,
  bindData: null,
  travelDetails: null,
  personalDetails: null,
  sessionData: null,
  moreInfoDraft: null,
  paymentStatus: "IDLE",
  policyResult: null,
};

const quoteFlowSlice = createSlice({
  name: "quoteFlow",
  initialState,
  reducers: {
    setQuoteFlowState(state, action: PayloadAction<Partial<QuoteFlowState>>) {
      Object.assign(state, action.payload);
    },
    setQuoteFlowQuoteData(state, action: PayloadAction<any | null>) {
      state.quoteData = action.payload;
    },
    setQuoteFlowBindData(state, action: PayloadAction<any | null>) {
      state.bindData = action.payload;
    },
    setQuoteFlowTravelDetails(state, action: PayloadAction<Partial<TravelDetailsType> | null>) {
      state.travelDetails = action.payload;
    },
    setQuoteFlowPersonalDetails(
      state,
      action: PayloadAction<Partial<PersonalDetailsType> | Record<string, any> | null>
    ) {
      state.personalDetails = action.payload;
    },
    setQuoteFlowSessionData(state, action: PayloadAction<any | null>) {
      state.sessionData = action.payload;
    },
    setQuoteFlowMoreInfoDraft(state, action: PayloadAction<Record<string, any> | null>) {
      state.moreInfoDraft = action.payload;
    },
    setQuoteFlowPaymentStatus(state, action: PayloadAction<QuoteFlowPaymentStatus>) {
      state.paymentStatus = action.payload;
    },
    setQuoteFlowPolicyResult(state, action: PayloadAction<QuoteFlowPolicyResult | null>) {
      state.policyResult = action.payload;
    },
    clearQuoteFlowPolicyResult(state) {
      state.policyResult = null;
    },
    clearQuoteFlowState() {
      return initialState;
    },
  },
});

export const {
  clearQuoteFlowState,
  setQuoteFlowBindData,
  setQuoteFlowMoreInfoDraft,
  setQuoteFlowPaymentStatus,
  setQuoteFlowPolicyResult,
  setQuoteFlowPersonalDetails,
  setQuoteFlowQuoteData,
  setQuoteFlowSessionData,
  setQuoteFlowState,
  setQuoteFlowTravelDetails,
  clearQuoteFlowPolicyResult,
} = quoteFlowSlice.actions;

export const selectQuoteFlowState = (state: RootState) => state.quoteFlow;

export default quoteFlowSlice.reducer;
